import React from "react";

import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import {
  getValidatorRulesForOrgUnit,
  requiredRule,
} from "@src/features/employee_management/utils/department_helper";
import {
  useCreateDepartmentMutation,
  useGetDepartmentsQuery,
} from "@src/services/slices/departmentsSlice";
import { CreateDepartmentPayload, Department } from "@src/types";
import { Form, Input, Modal, App, Flex } from "antd";
import "./create_department_modal.less";

export interface CreateDepartmentModalProps {
  setModalVisible?: (modalVisible: boolean) => void;
  modalVisible: boolean;
  record?: Department;
}

const CreateDepartmentModal: React.FC<CreateDepartmentModalProps> = ({
  setModalVisible,
  modalVisible,
  record,
}) => {
  const [form] = Form.useForm<Department>();
  const { message } = App.useApp();

  /* rtk queries */
  const { data: departmentsData } = useGetDepartmentsQuery();

  /* Lazy queries */
  const [createDepartment] = useCreateDepartmentMutation();

  async function onFinish(fieldsValue: Department) {
    const createDepartmentPayload: CreateDepartmentPayload = {
      departmentCode: fieldsValue.orgUnit,
      departmentName: fieldsValue.orgName,
    };

    await createDepartment(createDepartmentPayload)
      .unwrap()
      .then(() => {
        message.success("Successfully created department");
      })
      .catch(() => {
        console.log("Error creating department");
      });
    setModalVisible(false);
    closeModal();
  }

  function clearForm() {
    form.resetFields();
  }

  function closeModal() {
    clearForm();
    setModalVisible(false);
  }

  function checkIfOrgCodeExists(orgCodeInput: string) {
    return departmentsData?.some(
      (department: Department) => department.orgUnit === orgCodeInput
    );
  }

  function handleCancel() {
    form.resetFields();
    setModalVisible(false);
  }

  return (
    <div className="create-department-modal">
      <Modal
        open={modalVisible}
        closable={false}
        maskClosable={false}
        title={"Add new department"}
        onCancel={handleCancel}
        cancelButtonProps={{ icon: <CloseOutlined />, size: "large" }}
        okText={"Save"}
        onOk={() => form.submit()}
        okButtonProps={{
          icon: <CheckOutlined />,
          size: "large",
        }}
        width="470px"
      >
        <div className="modal-form">
          <Form
            id="departmentForm"
            layout="vertical"
            form={form}
            autoComplete="off"
            size="large"
            onFinish={onFinish}
            initialValues={record}
          >
            <Flex vertical gap={10}>
              <Form.Item
                label="Org Code"
                name="orgUnit"
                validateTrigger="onBlur"
                rules={getValidatorRulesForOrgUnit(checkIfOrgCodeExists)}
              >
                <Input
                  data-testid="orgUnit"
                  placeholder="Type Org Code: e.g. OEE-ED"
                />
              </Form.Item>
              <Form.Item
                label="Department Name"
                name="orgName"
                validateTrigger="onBlur"
                rules={[requiredRule]}
              >
                <Input
                  data-testid="orgName"
                  placeholder="Type Department Name"
                />
              </Form.Item>
            </Flex>
          </Form>
        </div>
      </Modal>
    </div>
  );
};

export default CreateDepartmentModal;
